/* StripeSyncStatusWidget — per-service Stripe sync status panel for the
 * Admin → Services tab.
 *
 * Now covers BOTH divisions Stripe pre-bakes products for:
 *   - Systems (managed services × tiers)
 *   - Academy (programs × tiers)
 *
 * Services e-commerce store is NOT pre-baked — it uses on-the-fly Stripe
 * Checkout sessions for variable inventory. We surface that mode clearly
 * in the division selector so admins know it's by design, not a gap.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCircle2, AlertTriangle, XCircle, RefreshCw, ChevronDown, ChevronRight, Lock, ShoppingCart } from 'lucide-react';

const API = `${(process.env.CRA_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BACKEND_URL)}/api`;

const STATE_CONFIG = {
  synced:  { label: 'Synced',         icon: CheckCircle2,  cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  drift:   { label: 'Price drift',    icon: AlertTriangle, cls: 'bg-amber-50 text-amber-800 border-amber-200' },
  missing: { label: 'Missing',        icon: XCircle,       cls: 'bg-rose-50 text-rose-700 border-rose-200' },
  partial: { label: 'Partial',        icon: AlertTriangle, cls: 'bg-amber-50 text-amber-800 border-amber-200' },
  skipped: { label: 'Stripe inactive', icon: Lock,         cls: 'bg-zinc-100 text-zinc-700 border-zinc-200' },
};

const EMPTY_TOTALS = {};

const DIVISIONS = [
  { key: 'systems', label: 'Systems',  status: 'admin/stripe/systems-status',  sync: 'admin/stripe/systems-sync',  noun: 'services' },
  { key: 'academy', label: 'Academy',  status: 'admin/stripe/academy-status',  sync: 'admin/stripe/academy-sync',  noun: 'programs' },
  { key: 'services', label: 'Services (store)', status: null, sync: null, noun: 'products' },
];

function StateBadge({ state, size = 'sm' }) {
  const cfg = STATE_CONFIG[state] || STATE_CONFIG.skipped;
  const Icon = cfg.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-bold uppercase tracking-wider ${cfg.cls} ${size === 'sm' ? 'text-[10px]' : 'text-[11px]'}`}
      data-testid={`stripe-state-${state}`}
    >
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
}

export default function StripeSyncStatusWidget() {
  const [division, setDivision] = useState('systems');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [lastSyncResult, setLastSyncResult] = useState(null);

  const activeDivision = DIVISIONS.find((d) => d.key === division) || DIVISIONS[0];

  const load = useCallback(async () => {
    if (!activeDivision.status) {
      setData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const r = await fetch(`${API}/${activeDivision.status}`, { credentials: 'include' });
      if (r.ok) setData(await r.json());
    } finally {
      setLoading(false);
    }
  }, [activeDivision]);

  useEffect(() => { setExpanded({}); setLastSyncResult(null); load(); }, [load]);

  const reSync = useCallback(async () => {
    if (!activeDivision.sync) return;
    setSyncing(true);
    setLastSyncResult(null);
    try {
      const r = await fetch(`${API}/${activeDivision.sync}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (r.ok) setLastSyncResult(await r.json());
      await load();
    } finally {
      setSyncing(false);
    }
  }, [load, activeDivision]);

  const totals = data?.totals ?? EMPTY_TOTALS;
  const services = data?.services || [];
  const skippedReason = data?.skipped_reason;

  const overallBadge = useMemo(() => {
    if (skippedReason) return 'skipped';
    if ((totals.drift || 0) > 0) return 'drift';
    if ((totals.missing || 0) > 0) return 'missing';
    return 'synced';
  }, [skippedReason, totals]);

  const isStoreDivision = activeDivision.key === 'services';

  return (
    <section className="rounded-2xl border border-[#E5E5E7] bg-white" data-testid="stripe-sync-widget">
      {/* Division tabs */}
      <div className="flex items-center gap-1 px-4 pt-3 border-b border-[#F2F2F4]">
        {DIVISIONS.map((d) => (
          <button
            key={d.key}
            type="button"
            onClick={() => setDivision(d.key)}
            data-testid={`stripe-division-tab-${d.key}`}
            className={`text-[11.5px] font-bold uppercase tracking-wider px-3 py-2 rounded-t-lg transition-colors ${
              division === d.key
                ? 'bg-[#fff8e7] text-[#0e0e10] border-x border-t border-[#c9a227]/40'
                : 'text-[#6E6E73] hover:text-[#0e0e10] hover:bg-[#fafafa]'
            }`}
          >
            {d.label}
          </button>
        ))}
      </div>

      {/* Store division — explanatory card, no sync needed */}
      {isStoreDivision ? (
        <div className="p-6" data-testid="stripe-store-division-info">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#fff8e7] border border-[#c9a227]/30 flex items-center justify-center flex-shrink-0">
              <ShoppingCart className="w-5 h-5 text-[#a8871a]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-[15px] font-extrabold text-[#0e0e10]">Services (e-commerce store)</h3>
                <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700">
                  <CheckCircle2 className="w-3 h-3" /> Connected — by design
                </span>
              </div>
              <p className="text-[12.5px] text-[#6E6E73] mt-2 leading-relaxed">
                The store has variable inventory + per-variant pricing. Each checkout
                creates a Stripe Checkout session on the fly with <code className="bg-zinc-100 px-1 rounded text-[11px]">price_data</code>{' '}
                — Stripe charges the customer, the order is recorded server-side, and
                no pre-baked Stripe Products are needed.
              </p>
              <p className="text-[12.5px] text-[#6E6E73] mt-2 leading-relaxed">
                Pre-baking a Stripe Product per SKU would force a sync every time you
                add a colour, change a price, or restock — which is the wrong pattern
                for a fast-moving catalogue. The current setup is the recommended Stripe
                approach for catalog-style commerce.
              </p>
              <ul className="text-[11.5px] text-[#0e0e10] mt-3 space-y-1.5">
                <li>• Customer flow: <code className="bg-zinc-100 px-1 rounded">/api/store/checkout</code> → Stripe Checkout → webhook → order paid</li>
                <li>• Refunds: handled in the Stripe Dashboard or the Admin → Orders panel</li>
                <li>• Tax + shipping: configured per Stripe Checkout session at order time</li>
              </ul>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Header for managed divisions */}
          <header className="flex items-center justify-between gap-3 flex-wrap px-5 pt-4 pb-3 border-b border-[#F2F2F4]">
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-[15px] font-extrabold text-[#0e0e10]">
                  Stripe sync — {activeDivision.label} {activeDivision.noun}
                </h3>
                {!loading && <StateBadge state={overallBadge} size="md" />}
              </div>
              <p className="text-[12.5px] text-[#6E6E73] mt-0.5">
                {loading ? 'Loading status…' : (
                  skippedReason
                    ? <>Stripe is inactive: <code className="text-[11px] bg-zinc-100 px-1 rounded">{skippedReason}</code> — set a real <code className="bg-zinc-100 px-1 rounded">STRIPE_API_KEY</code> in <code className="bg-zinc-100 px-1 rounded">backend/.env</code> and redeploy.</>
                    : <>{totals.synced} synced · {totals.drift} drift · {totals.missing} missing of {totals.services} {activeDivision.noun}</>
                )}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={load}
                data-testid="stripe-sync-refresh"
                className="inline-flex items-center gap-1.5 text-[11.5px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full border border-[#E5E5E7] bg-white hover:bg-[#fff8e7] hover:border-[#c9a227]/50 transition-colors"
              >
                <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
                Reload
              </button>
              <button
                type="button"
                onClick={reSync}
                disabled={syncing || !!skippedReason}
                data-testid="stripe-sync-trigger"
                className="inline-flex items-center gap-1.5 text-[11.5px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full text-[#0e0e10] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                style={{ background: 'linear-gradient(90deg,#e6c35a 0%,#c9a227 100%)' }}
                title={skippedReason ? 'Set a real Stripe key first' : 'Re-run the idempotent sync against Stripe'}
              >
                <RefreshCw className={`w-3 h-3 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? 'Syncing…' : 'Re-sync now'}
              </button>
            </div>
          </header>

          {/* Last sync result toast */}
          {lastSyncResult && (
            <div className="px-5 py-2 text-[11.5px] bg-emerald-50/50 border-b border-emerald-100 text-emerald-800 flex items-center gap-2 flex-wrap" data-testid="stripe-sync-last-result">
              Last sync: created {lastSyncResult.products_created || 0} products, {lastSyncResult.prices_created || 0} prices ·
              replaced {lastSyncResult.prices_replaced || 0} ·
              archived {(lastSyncResult.products_archived || 0) + (lastSyncResult.prices_archived || 0)} ·
              noop {lastSyncResult.prices_noop || 0}
              {(lastSyncResult.errors || []).length > 0 && (
                <span className="text-rose-700">· {lastSyncResult.errors.length} error(s)</span>
              )}
            </div>
          )}

          {/* Service rows */}
          {!loading && (
            <div className="divide-y divide-[#F2F2F4]">
              {services.map((svc) => {
                const isOpen = !!expanded[svc.slug];
                return (
                  <div key={svc.slug} className="px-5 py-3" data-testid={`stripe-svc-row-${svc.slug}`}>
                    <button
                      type="button"
                      onClick={() => setExpanded((e) => ({ ...e, [svc.slug]: !e[svc.slug] }))}
                      className="w-full flex items-center justify-between gap-3 text-left hover:bg-[#fafafa] -mx-2 px-2 py-1 rounded transition-colors"
                    >
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        {isOpen ? <ChevronDown className="w-4 h-4 text-[#6E6E73] flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-[#6E6E73] flex-shrink-0" />}
                        <span className="font-bold text-[13px] text-[#0e0e10] truncate">{svc.name}</span>
                        <code className="text-[10.5px] text-[#a8871a] bg-[#fff8e7] px-1.5 py-0.5 rounded flex-shrink-0">{svc.slug}</code>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-[11px] text-[#6E6E73]">
                          {svc.synced_tiers}/{svc.tiers.length} tiers synced
                        </span>
                        <StateBadge state={svc.overall_state} />
                      </div>
                    </button>

                    {isOpen && (
                      <div className="mt-2 ml-6 grid grid-cols-1 md:grid-cols-3 gap-2">
                        {svc.tiers.map((t) => (
                          <div
                            key={t.lookup_key}
                            className="rounded-lg border border-[#E5E5E7] p-2.5 bg-[#fafafa]"
                            data-testid={`stripe-tier-${t.lookup_key}`}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-[12px] font-bold text-[#0e0e10]">{t.tier_name}</span>
                              <StateBadge state={t.state === 'skipped_free' ? 'skipped' : t.state} />
                            </div>
                            <div className="text-[10.5px] text-[#6E6E73] mt-1 space-y-0.5">
                              <div><code className="text-[#a8871a]">{t.lookup_key}</code></div>
                              <div>
                                Catalog: <strong>{t.expected_amount_human || `$${(t.expected_amount/100).toFixed(2)}`}</strong>
                                {t.stripe_amount != null && t.stripe_amount !== t.expected_amount && (
                                  <span className="text-rose-700"> · Stripe: ${(t.stripe_amount/100).toFixed(2)}</span>
                                )}
                              </div>
                              {t.stripe_price_id && (
                                <div className="truncate" title={t.stripe_price_id}>
                                  ID: <code className="text-[10px]">{t.stripe_price_id}</code>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </section>
  );
}
